import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, PenTool, BarChart3, BookOpen, Target, ChevronRight, Briefcase } from 'lucide-react';

// Feature Card Component - All features unlocked for purchased optionals
const FeatureCard = ({ feature, onNavigate }) => {
  const IconComponent = feature.icon;
  const colorStyles = {
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-200 hover:border-amber-400 hover:bg-amber-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-200 hover:border-rose-400 hover:bg-rose-100',
  };
  const iconBgStyles = {
    indigo: 'bg-indigo-100',
    emerald: 'bg-emerald-100',
    amber: 'bg-amber-100',
    rose: 'bg-rose-100',
  };

  const handleClick = () => {
    // All features accessible - user has purchased this optional
    onNavigate(`${feature.path}?tab=${feature.tab}`);
  };

  return (
    <div
      onClick={handleClick}
      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${colorStyles[feature.color]}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${iconBgStyles[feature.color]} flex items-center justify-center`}>
          <IconComponent className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-slate-900 text-sm">{feature.title}</h4>
          <p className="text-xs text-slate-500 truncate">{feature.description}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  );
};

// Paper Section Component
const PaperSection = ({ paperNum, title, subtitle, features, gradient, borderColor, onNavigate }) => (
  <Card className={`overflow-hidden border-2 ${borderColor}`}>
    {/* Paper Header */}
    <div className={`${gradient} px-6 py-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-2xl">{paperNum === 1 ? '📘' : '📙'}</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{title}</h2>
            <p className="text-white/80 text-sm">{subtitle}</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="bg-white/20 text-white hover:bg-white/30 border-0"
          onClick={() => onNavigate(`/optional/pub-admin/paper${paperNum}`)}
        >
          Open Paper {paperNum}
        </Button>
      </div>
    </div>
    
    {/* Features Grid */}
    <CardContent className="p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {features.map((feature) => (
          <FeatureCard key={feature.id} feature={feature} onNavigate={onNavigate} />
        ))}
      </div>
    </CardContent>
  </Card>
);

/**
 * Public Administration Suite Page - All features unlocked for purchased optional
 */
const PubAdminSuitePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const paper1Features = [
    {
      id: 'answer-writing',
      icon: PenTool,
      title: 'Answer Writing',
      description: 'Top Ranker Mode',
      path: '/optional/pub-admin/paper1',
      tab: 'generate',
      color: 'indigo'
    },
    {
      id: 'evaluation',
      icon: BarChart3,
      title: 'Evaluation',
      description: 'Examiner Mode',
      path: '/optional/pub-admin/paper1',
      tab: 'evaluate',
      color: 'emerald'
    },
    {
      id: 'pyq',
      icon: BookOpen,
      title: 'PYQ Analysis',
      description: '30 Years Data',
      path: '/optional/pub-admin/paper1',
      tab: 'pyq',
      color: 'amber'
    },
    {
      id: 'expected',
      icon: Target,
      title: 'Expected Qs',
      description: 'Predictions',
      path: '/optional/pub-admin/paper1',
      tab: 'expected',
      color: 'rose'
    }
  ];

  const paper2Features = [
    {
      id: 'answer-writing',
      icon: PenTool,
      title: 'Answer Writing',
      description: 'Top Ranker Mode',
      path: '/optional/pub-admin/paper2',
      tab: 'generate',
      color: 'indigo'
    },
    {
      id: 'evaluation',
      icon: BarChart3,
      title: 'Evaluation',
      description: 'Examiner Mode',
      path: '/optional/pub-admin/paper2',
      tab: 'evaluate',
      color: 'emerald'
    },
    {
      id: 'pyq',
      icon: BookOpen,
      title: 'PYQ Analysis',
      description: '30 Years Data',
      path: '/optional/pub-admin/paper2',
      tab: 'pyq',
      color: 'amber'
    },
    {
      id: 'expected',
      icon: Target,
      title: 'Expected Qs',
      description: 'Predictions',
      path: '/optional/pub-admin/paper2',
      tab: 'expected',
      color: 'rose'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 via-gray-700 to-slate-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Button
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10 mb-4"
            onClick={() => navigate('/optional')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Optional Hub
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Briefcase className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Public Administration Suite</h1>
              <p className="text-slate-300 text-sm">4-Engine System • Top Ranker Admin Mode</p>
            </div>
          </div>
          
          {/* Engine Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-white/20 text-white border-0 text-xs">
              <PenTool className="w-3 h-3 mr-1" /> Writer
            </Badge>
            <Badge className="bg-white/20 text-white border-0 text-xs">
              <BarChart3 className="w-3 h-3 mr-1" /> Evaluator
            </Badge>
            <Badge className="bg-white/20 text-white border-0 text-xs">
              <BookOpen className="w-3 h-3 mr-1" /> PYQ Analyzer
            </Badge>
            <Badge className="bg-white/20 text-white border-0 text-xs">
              <Target className="w-3 h-3 mr-1" /> Predictor
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Paper 1 Section */}
        <PaperSection
          paperNum={1}
          title="Paper I"
          subtitle="Administrative Theory"
          features={paper1Features}
          gradient="bg-gradient-to-r from-slate-600 to-gray-700"
          borderColor="border-slate-200 hover:border-slate-300"
          onNavigate={navigate}
        />

        {/* Paper 2 Section */}
        <PaperSection
          paperNum={2}
          title="Paper II"
          subtitle="Indian Administration"
          features={paper2Features}
          gradient="bg-gradient-to-r from-gray-600 to-slate-700"
          borderColor="border-gray-200 hover:border-gray-300"
          onNavigate={navigate}
        />

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 text-center bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200">
            <p className="text-2xl font-bold text-slate-600">10M</p>
            <p className="text-xs text-slate-600">140-160 words</p>
          </Card>
          <Card className="p-4 text-center bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200">
            <p className="text-2xl font-bold text-gray-600">15M</p>
            <p className="text-xs text-slate-600">230-260 words</p>
          </Card>
          <Card className="p-4 text-center bg-gradient-to-br from-slate-100 to-gray-100 border-slate-200">
            <p className="text-2xl font-bold text-slate-700">20M</p>
            <p className="text-xs text-slate-600">320-360 words</p>
          </Card>
        </div>

        {/* Key Topics */}
        <Card className="p-6 border-slate-200">
          <h3 className="font-bold text-lg text-slate-900 mb-4">📋 Key Areas</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Badge variant="outline" className="justify-center py-2">Administrative Thought</Badge>
            <Badge variant="outline" className="justify-center py-2">Accountability</Badge>
            <Badge variant="outline" className="justify-center py-2">Personnel Admin</Badge>
            <Badge variant="outline" className="justify-center py-2">Financial Admin</Badge>
            <Badge variant="outline" className="justify-center py-2">District Admin</Badge>
            <Badge variant="outline" className="justify-center py-2">Civil Services</Badge>
            <Badge variant="outline" className="justify-center py-2">Local Government</Badge>
            <Badge variant="outline" className="justify-center py-2">Admin Reforms</Badge>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PubAdminSuitePage;
